import {
    StyledGroupedInfoTileBackground,
    TitleSize
} from "@components/tiles/incomeExpenseOverview/IncomeExpenseOverview.styles";
import { WithOData, withOData } from "@odata/withOData";
import { CbaEntryLedgerVariants } from "@pages/reports/cbaEntryLedger/CbaEntryLedgerDef";
import React, { memo, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { Status } from "../../../enums";
import { ROUTE_CBA_ENTRY_LEDGER } from "../../../routes";
import { DashboardTileType, IDashboardTileComponentProps, IInfoTileInfo } from "../../dashboard";
import DashboardTileHeader from "../../dashboard/DashboardTileHeader";
import { getDrillDownNavParams } from "../../drillDown/DrillDown.utils";
import InfoTile, { infoTileCurrencyFormatter } from "../infoTile/InfoTile";
import { GroupedInfoTileContentWrapper, GroupInfoTileHeader } from "../infoTile/InfoTile.styles";
import { getIncomeExpenseOverviewData, IIncomeExpenseOverviewData } from "./IncomeExpenseOverview.utils";

interface IProps extends IDashboardTileComponentProps, WithOData {
}

const IncomeExpenseOverview: React.FC<IProps> = ({ inEditMode }) => {
    const { t } = useTranslation("Home");
    const context = useContext(AppContext);


    const [loadedData, setData] = useState<IIncomeExpenseOverviewData>(null);
    const [isLoading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        let mounted = true;

        try {
            getIncomeExpenseOverviewData(context)
                    .then(data => mounted && setData(data))
                    .finally(() => mounted && setLoading(false));
        } catch (e) {
            if (mounted) {
                setData(null);
            }
        }

        return () => {
            mounted = false;
        };
    }, [context]);

    const incomeInfo: IInfoTileInfo = useMemo(() => ({
        type: DashboardTileType.Info,
        size: { w: 2, h: 1 },
        title: t("Home:IncomeExpenseOverview.Income"),
        infoData: [{
            value: loadedData?.income?.tax,
            formatter: infoTileCurrencyFormatter,
            label: t("Home:IncomeExpenseOverview.Tax"),
            severity: Status.Success,
            unit: "Kč"
        }, {
            value: loadedData?.income?.nontax,
            formatter: infoTileCurrencyFormatter,
            label: t("Home:IncomeExpenseOverview.NonTax"),
            unit: "Kč"
        }],
    }), [t, loadedData]);

    const expenseInfo: IInfoTileInfo = useMemo(() => ({
        type: DashboardTileType.Info,
        size: { w: 2, h: 1 },
        title: t("Home:IncomeExpenseOverview.Expense"),
        infoData: [{
            value: loadedData?.expense?.tax,
            formatter: infoTileCurrencyFormatter,
            label: t("Home:IncomeExpenseOverview.Tax"),
            unit: "Kč"
        }, {
            value: loadedData?.expense?.nontax,
            formatter: infoTileCurrencyFormatter,
            label: t("Home:IncomeExpenseOverview.NonTax"),
            unit: "Kč"
        }],
    }), [t, loadedData]);

    const titleSize: TitleSize = useMemo(() => {
        const expenseLength = (loadedData?.expense?.tax ?? 0).toString().length + (loadedData?.expense?.nontax ?? 0).toString().length;
        const incomeLength = (loadedData?.income?.tax ?? 0).toString().length + (loadedData?.income?.nontax ?? 0).toString().length;
        const width = Math.max(expenseLength, incomeLength, 10);
        return width < 16 ? width < 11 ? TitleSize.large : TitleSize.medium : TitleSize.small;
    }, [loadedData]);

    return (
            <StyledGroupedInfoTileBackground
                    $fontSize={titleSize}
                    inEditMode={inEditMode}
                    overflowed
                    to={getDrillDownNavParams({
                        route: ROUTE_CBA_ENTRY_LEDGER,
                        context,
                        customQueryString: `variant=${CbaEntryLedgerVariants.CZK}`
                    })}
                    isLoading={isLoading}>
                <GroupInfoTileHeader>
                    <DashboardTileHeader><b>{t("Home:IncomeExpenseOverview.Heading")}</b></DashboardTileHeader>
                </GroupInfoTileHeader>

                {!isLoading && (
                        <GroupedInfoTileContentWrapper cols={1} rows={2}>
                            <InfoTile inEditMode={inEditMode} info={incomeInfo} isGrouped/>
                            <InfoTile inEditMode={inEditMode} info={expenseInfo} isGrouped/>
                        </GroupedInfoTileContentWrapper>
                )}
            </StyledGroupedInfoTileBackground>
    );
};

export default memo(withOData(IncomeExpenseOverview));